import {Box, Button, Flex, Grid, Radio, RadioGroup, Select, Stack, Text} from "@chakra-ui/react";
import {AppContext, AppContextI} from "../../../context/ContextWrapper";
import {ChangeEvent, useContext, useState} from "react";
import {MarkerSpot, RulesLogic, Spot, SpotInfo, TriggerRule} from "../../../types";
import {v4 as uuidv4} from 'uuid';
import RuleDetails from "./RuleDetails";
import {useConfirmDialog} from "../../../common/ConfirmDialogProvider";


type Props = {
    spot: Spot,
    allSpotsInfo: SpotInfo[]
}

const SpotListRules = ({spot, allSpotsInfo}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const {confirm} = useConfirmDialog();
    const [copyFromSpotId, setCopyFromSpotId] = useState<string | undefined>("-1")

    const markerSpot: MarkerSpot = spot as MarkerSpot;

    const logicHandler = (val: string) => {
        appContext.updateSpot(markerSpot, {
            ...markerSpot,
            triggerRules: {
                logic: val,
                rules: markerSpot.triggerRules?.rules
            }
        } as MarkerSpot)
    };

    const copyRules = async () => {
        const approved = await confirm({
            title: "Replace rules",
            body: "Do you want to replace the current rules?",
            confirmText: "Replace",
            isDestructive: true
        });
        if (!approved) {
            setCopyFromSpotId("-1");
            return;
        }
        const copyFromSpot: Spot | undefined = appContext.getSpot(copyFromSpotId);
        if (copyFromSpot) {
            appContext.updateSpot(markerSpot, {
                ...markerSpot,
                triggerRules: copyFromSpot.triggerRules
            } as MarkerSpot)
            setCopyFromSpotId("-1");
        }
    };


    const changeCopyFromSpotId = (evt: ChangeEvent<HTMLSelectElement>) => {
        setCopyFromSpotId(evt.target.value)
    };

    return <>
        <Grid gridTemplateColumns={"max-content 1fr max-content"} columnGap={4} marginBottom={8}>
            <Text>Replace with rules from:</Text>

            <Box>
                <Select value={copyFromSpotId}
                        onChange={changeCopyFromSpotId}
                        size={"sm"}
                        disabled={!appContext.isDraft()}
                >
                    <option value={"-1"}>Select a spot</option>
                    {
                        allSpotsInfo
                            .filter(sp => sp.id !== markerSpot.id)
                            .map((spotInfo: SpotInfo) => {
                                return <option key={spotInfo.id} value={spotInfo.id}>{spotInfo.name}</option>
                            })
                    }
                </Select>
            </Box>
            <Button disabled={!appContext.isDraft() || "-1" === copyFromSpotId || !appContext.isDraft()}
                    onClick={copyRules}
            >
                Go</Button>
        </Grid>

        <Box width={"120px"}>
            <Box float={"left"} mr={"2"}>Logic:</Box>
            <RadioGroup value={markerSpot.triggerRules?.logic} onChange={logicHandler}
                        isDisabled={!appContext.isDraft()}>
                <Stack direction='row'>
                    <Radio
                        value={RulesLogic.AND.toString()}
                        id={RulesLogic.AND.toString()}
                    >
                        AND
                    </Radio>
                    <Radio
                        value={RulesLogic.OR.toString()}
                        id={RulesLogic.OR.toString()}
                    >
                        OR
                    </Radio>
                </Stack>
            </RadioGroup>
        </Box>

        <Grid templateColumns={"min-content 1fr min-content"} marginTop={"8px"}>
            {
                appContext.isSelected(spot) ?
                    <>
                        <Box/>

                        <Flex columnGap={"4px"}>

                            <Button size={"sm"}
                                    onClick={() => {
                                        const newRules: TriggerRule[] = [...markerSpot.triggerRules?.rules ? markerSpot.triggerRules.rules : [],
                                            {
                                                id: uuidv4(),
                                                type: "Spot",
                                                spotId: undefined,
                                                eventType: "SPOT_TRIGGERED"
                                            } as TriggerRule
                                        ];
                                        appContext.updateSpot(markerSpot, {
                                            ...markerSpot,
                                            triggerRules: {
                                                logic: markerSpot.triggerRules?.logic ? markerSpot.triggerRules?.logic : "And",
                                                rules: newRules
                                            }
                                        } as MarkerSpot)
                                    }
                                    }
                                    disabled={!appContext.isDraft()}
                            >
                                Add rule
                            </Button>
                        </Flex>
                    </>
                    : null
            }
        </Grid>
        <Grid gridTemplateColumns={"max-content 1fr 24px"} rowGap={"36px"} marginTop={"8px"}>
            {
                markerSpot.triggerRules?.rules?.map((rule: TriggerRule, index: number) =>
                    <RuleDetails key={"rule_" + rule.id} rule={rule}
                                 markerSpot={markerSpot} index={index}
                                 allSpotsInfo={allSpotsInfo}
                    />
                )
            }
        </Grid>
    </>
}

export default SpotListRules;